import React from 'react';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PostHogProvider } from 'posthog-js/react';

// eslint-disable-next-line import/no-extraneous-dependencies
import { useMediaQuery } from 'react-responsive';
import { PortalProvider, NotificationsProvider } from './providers';
import { AuthenticationProvider } from './features/authentication';
import { OrganizationsProvider, UsersProvider, RolesProvider } from './features/organization';

import { AppStyle } from './AppStyle';
import { AppRouter } from './AppRouter';
import { MobileBanner } from './components';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false // default: true
    }
  }
});

const App = () => {
  const { NODE_ENV } = process.env;
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });

  const contentSecurityPolicy =
    NODE_ENV === 'production'
      ? `default-src 'self' *.supper.co *.sentry.io *.merge.dev *.useparagon.com;
        script-src 'self' 'unsafe-eval' *.sentry.io *.merge.dev *.useparagon.com *.supper.co https://us-assets.i.posthog.com;
        font-src 'self' *.supper.co fonts.gstatic.com fonts.googleapis.com;
        img-src *;
        style-src 'self' 'unsafe-inline' *.supper.co fonts.googleapis.com;
        connect-src 'self' *.supper.co *.sentry.io *.merge.dev *.useparagon.com wss://api.supper.co https://us.i.posthog.com;`
      : `default-src 'self' *.supper.co *.sentry.io *.merge.dev *.useparagon.com;
        script-src 'self' 'unsafe-eval' *.sentry.io *.merge.dev *.useparagon.com *.supper.co https://us-assets.i.posthog.com;
        font-src 'self' *.supper.co fonts.gstatic.com fonts.googleapis.com;
        img-src *;
        style-src 'self' 'unsafe-inline' *.supper.co fonts.googleapis.com;
        connect-src 'self' *.supper.co *.sentry.io *.merge.dev *.useparagon.com wss://api.supper.co https://us.i.posthog.com http://localhost:3001 ws://localhost:3001;`;

  return (
    <HelmetProvider>
      <Helmet>
        <meta httpEquiv='Content-Security-Policy' content={contentSecurityPolicy} />
        <link rel='preconnect' href='https://fonts.googleapis.com' />
        <link rel='preconnect' href='https://fonts.gstatic.com' crossOrigin='anonymous' />
        <link
          href='https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700'
          rel='stylesheet'
        />
      </Helmet>
      <PostHogProvider
        apiKey={process.env.REACT_APP_PUBLIC_POSTHOG_KEY}
        options={{
          api_host: process.env.REACT_APP_PUBLIC_POSTHOG_HOST,
          before_send: (event) => {
            if (!event) {
              return null;
            }

            if (event.properties.$current_url) {
              return {
                ...event,
                properties: {
                  ...event.properties,
                  $current_url: event.properties.$current_url.replace(
                    /(access_token)=[^&]+/g,
                    '$1=REDACTED'
                  )
                }
              };
            }

            return event;
          }
        }}
      />
      <AppStyle />
      {isMobile ? (
        <MobileBanner />
      ) : (
        <AuthenticationProvider>
          <QueryClientProvider client={queryClient}>
            <NotificationsProvider>
              <RolesProvider>
                <OrganizationsProvider>
                  <UsersProvider>
                    <PortalProvider>
                      <AppRouter />
                    </PortalProvider>
                  </UsersProvider>
                </OrganizationsProvider>
              </RolesProvider>
            </NotificationsProvider>
          </QueryClientProvider>
        </AuthenticationProvider>
      )}
    </HelmetProvider>
  );
};

export { App };
